// AdminGrowthArcsPage.tsx — the growth review console (B1: restructured drill-down).
//
// Navigation is a drill-down, not flat tabs:
//   Arc  →  Video (storyboard: segments in sequence)  →  Segment detail (all render inputs)
//
// - Arc view: the arc's videos in release order, each with its rolled-up lifecycle state
//   (pre / production / post / post_pre) — the arc is a container showing a MIX of video states.
// - Video view: the storyboard strip — segments as image cards with status; click a segment → detail.
// - Segment detail: every input that affects rendering (script, voice, motion, frame, caption, cast,
//   duration, artefact, status). Editable now: on-screen text, re-roll regenerate, cast (edit-continue),
//   retry/skip/reseed. Frame/motion are shown with a deferred ("redirect coming") edit affordance —
//   built as first-class rows so the future redirect (needs the pantry station) is additive.
//
// Content-reviewer-facing by design: named things you can see and act on, not engine step ids.
// Reachable at /admin/growth/arcs/:arcId.
import { useProgress } from "@/components/ui/ProgressContext.tsx";
import FinishAndPostPanel from "./FinishAndPostPanel.tsx";
import { AdminNeedsYou } from "../../components/engine/AdminNeedsYou.tsx";   // (TF-6b-1)
import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi, type CharacterListItem } from "../../api/admin.ts";
import {
  growthApi, growthReviewApi,
  videoState, videoStateBadge,
  type GrowthArcReview, type GrowthArcReviewVideo, type ArcScore,
  type StoryboardBeat, type SegmentDetail,
} from "../../api/growth.ts";
import { ConceptFrames, PromptEditModal } from "./ConceptFrameGrid.tsx";
import { opsApi, type RunStep } from "../../api/ops.ts";

const wrap: React.CSSProperties = { padding: 16 };
const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff", marginBottom: 10 };
const btn: React.CSSProperties = { padding: "6px 12px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13 };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const input: React.CSSProperties = { padding: "6px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, width: "100%" };
const muted: React.CSSProperties = { fontSize: 12, color: "#6b7280" };

// step-status → colour (shared across the console).
function stepStatusColor(status: string): string {
  if (status === "failed") return "#dc2626";
  if (status === "awaiting_gate") return "#d97706";
  if (status === "succeeded" || status === "done" || status === "skipped") return "#16a34a";
  return "#2563eb";
}

// tf-041 (Track D): which failing steps plausibly indicate a bad REDRESS rather than a transient
// failure — the identity/frame chain. Reseed is offered only on these (not every step).
const IDENTITY_STEP_IDS = new Set(["character_direct", "image_direct", "image_verify", "generate_image"]);

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL 1 — ARC VIEW: the container. Videos in sequence, each with its rolled-up state.
// ═══════════════════════════════════════════════════════════════════════════
export function AdminGrowthArcsPage() {
  const { arcId = "" } = useParams();
  const [detail, setDetail] = useState<GrowthArcReview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openSeq, setOpenSeq] = useState<number | null>(null); // which video is drilled into

  const load = useCallback(() => {
    setErr(null);
    growthApi.arcDetail(arcId).then(setDetail).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [arcId]);
  useEffect(load, [load]);

  if (err) return <div style={wrap}><div style={{ ...card, color: "#b91c1c" }}>Failed: {err} <button style={btn} onClick={load}>Retry</button></div></div>;
  if (!detail) return <div style={wrap}><div style={muted}>Loading…</div></div>;

  const openVideo = detail.videos.find((v) => v.seq === openSeq) ?? null;

  return (
    <div style={wrap}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <Link to="/admin/growth" style={{ ...btn, textDecoration: "none" }}>← Growth</Link>
        <button style={btn} onClick={load}>Refresh</button>
        {openVideo && (
          <button style={btn} onClick={() => setOpenSeq(null)}>← All videos</button>
        )}
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>{detail.niche} · {detail.patternId}</h1>
      <p style={{ ...muted, marginBottom: 12 }}>{detail.premise}</p>

      {/* Review entry (operator stage-visibility): a clear way to START/continue candidate review from
          the arc, not only from the queue. Image/carousel arcs → the spread review; video arcs review
          via the storyboard drill-down below (open a video). Always offer a link back to the queue. */}
      {(() => {
        return (
          <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={muted}>Review the visuals in the grid below — generate, regenerate, and approve each before producing.</span>
            <span style={{ flex: 1 }} />
            <Link to="/admin/growth/queue" style={{ ...btn, textDecoration: "none" }}>Review queue →</Link>
          </div>
        );
      })()}

      {/* Concept-frame checkpoint (cheapest tier): 1 image per video → approve/regenerate BEFORE the
          storyboard's multiple images and the clips. Only for video items. */}
      {detail.videos.some((v) => v.mode === "video" || v.mode === "carousel" || v.mode === "image") && !openVideo && (
        <ConceptFrames arcId={arcId} />
      )}

      {openVideo ? (
        // LEVEL 2 — drilled into one video: its storyboard.
        <VideoStoryboard arcId={arcId} video={openVideo} onChanged={load} />
      ) : (
        // LEVEL 1 — the arc's videos + a state-mix summary.
        <>
          <ArcStateSummary videos={detail.videos} />
          <ArcScorePanel arcId={arcId} />
          {detail.videos.length === 0 ? (
            <div style={card}>This arc has no videos.</div>
          ) : (
            detail.videos.map((v) => (
              <ArcVideoRow key={v.seq} video={v} onOpen={() => setOpenSeq(v.seq)} />
            ))
          )}
        </>
      )}
    </div>
  );
}

// A one-line roll-up of the video-state mix (videos finish independently, so an arc is normally a mix).
function ArcStateSummary({ videos }: { videos: GrowthArcReviewVideo[] }) {
  const counts = videos.reduce<Record<string, number>>((acc, v) => {
    const s = videoState({ runId: v.runId, assetUrl: v.assetUrl });   // no steps here → coarse (never post_pre)
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});
  const order: Array<["pre" | "production" | "post" | "post_pre", string]> = [
    ["post", "post"], ["production", "rendering"], ["post_pre", "revising"], ["pre", "pre"],
  ];
  const parts = order.filter(([k]) => counts[k]).map(([k, label]) => `${counts[k]} ${label}`);
  return (
    <div style={{ ...card, background: "#f9fafb", display: "flex", gap: 10, alignItems: "center" }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{videos.length} video{videos.length === 1 ? "" : "s"}</span>
      <span style={muted}>{parts.length ? parts.join(" · ") : "—"}</span>
    </div>
  );
}

function ArcVideoRow({ video, onOpen }: { video: GrowthArcReviewVideo; onOpen: () => void }) {
  // Coarse state at the list level (no step data fetched here — that happens on drill-in).
  const state = videoState({ runId: video.runId, assetUrl: video.assetUrl });
  const badge = video.postedAt ? { label: "Posted", color: "#16a34a" } : videoStateBadge(state);
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>#{video.seq + 1} · {video.role} · {video.mode}</div>
          <div style={{ ...muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {video.script ? video.script.slice(0, 120) : "(no script)"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontWeight: 600, color: badge.color }}>{badge.label}</span>
          {video.runId
            ? <button style={btn} onClick={onOpen}>Open storyboard →</button>
            : <span style={muted}>not produced yet</span>}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL 2 — VIDEO STORYBOARD: segments in sequence as image cards; click → segment detail.
// ═══════════════════════════════════════════════════════════════════════════
function VideoStoryboard({ arcId, video, onChanged }: {
  arcId: string; video: GrowthArcReviewVideo; onChanged: () => void;
}) {
  const seq = video.seq;
  const [board, setBoard] = useState<{ status: string; beats: StoryboardBeat[] } | null>(null);
  const [boardErr, setBoardErr] = useState<string | null>(null);
  const [steps, setSteps] = useState<RunStep[] | null>(null);
  const [cmsId, setCmsId] = useState<string | null>(null);
  const [openBeat, setOpenBeat] = useState<string | null>(null);

  const loadBoard = useCallback(() => {
    setBoardErr(null);
    growthReviewApi.storyboard(arcId, seq)
      .then((r) => setBoard({ status: r.status, beats: r.beats }))
      .catch((e) => setBoardErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setBoardErr)
  }, [arcId, seq]);

  const loadSteps = useCallback(() => {
    if (!video.runId) return;
    opsApi.runSteps(video.runId)
      .then((r) => { setSteps(r.steps); setCmsId(r.run?.cmsId ?? null); })
      .catch(() => { /* steps are supplementary; storyboard still renders */ });
  }, [video.runId]);

  useEffect(() => { loadBoard(); loadSteps(); }, [loadBoard, loadSteps]);

  // per-segment status from steps.
  const beatStepStatus = (beatId: string): string | null => {
    const bs = (steps ?? []).filter((s) => s.beatId === beatId);
    if (!bs.length) return null;
    if (bs.some((s) => s.status === "failed")) return "failed";
    if (bs.some((s) => s.status === "awaiting_gate")) return "awaiting_gate";
    if (bs.some((s) => ["blocked", "ready", "running", "dispatched"].includes(s.status))) return "in progress";
    return "succeeded";
  };

  const rolled = videoState({ runId: video.runId, assetUrl: video.assetUrl }, steps ?? undefined);
  const badge = videoStateBadge(rolled);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700 }}>#{seq + 1} · {video.role} · {video.mode}</h2>
        <span style={{ fontWeight: 600, color: badge.color }}>{badge.label}</span>
        <button style={btn} onClick={() => { loadBoard(); loadSteps(); }}>Refresh</button>
        {rolled !== "pre" && video.runId && (
          <ApproveButton arcId={arcId} seq={seq} onDone={() => { loadBoard(); loadSteps(); onChanged(); }} />
        )}
        {/* Only a live, never-finished run: a finished video's rework is not cancelled from here. */}
        {rolled === "production" && video.runId && !["cancelled", "succeeded", "failed"].includes(video.orderStatus ?? "") && (
          <CancelRunButton arcId={arcId} seq={seq} onDone={() => { loadBoard(); loadSteps(); onChanged(); }} />
        )}
      </div>

      {/* (TF-6b-1) the question this video's run waits on (a growth run has no video page): answered with growth's key */}
      {video.runId && <AdminNeedsYou runId={video.runId} onAnswered={() => { loadBoard(); loadSteps(); }} />}

      {/* Phase 4b — Finish & Post a COMPLETED growth VIDEO (music, branding, platform cuts). The finished video becomes
          the one to post. Not for carousels / stills (no post-production for them). */}
      {video.mode === "video" && video.runId && video.orderStatus === "succeeded" && (
        <div style={{ ...card, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Finish & Post</div>
          {video.finalUrl && <div style={{ fontSize: 12, color: "#16a34a", marginBottom: 6 }}>Finished — this is the version to post.</div>}
          <FinishAndPostPanel productionRunId={video.runId} callback="growth" />
        </div>
      )}

      {boardErr && (
        <div style={{ ...card, color: "#b45309", background: "#fffbeb" }}>
          {/* 409 = run not parked at a reviewable gate yet — an expected, informative state */}
          {boardErr.includes("409") || boardErr.toLowerCase().includes("not")
            ? "This video isn't at a reviewable stage yet — the storyboard appears when it parks for review."
            : `Couldn't load storyboard: ${boardErr}`}
        </div>
      )}
      {!board && !boardErr && <div style={muted}>Loading storyboard…</div>}

      {board && board.beats.length === 0 && <div style={card}>No segments to show yet.</div>}

      {board && board.beats.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginBottom: 12 }}>
          {board.beats.map((b) => {
            const ss = beatStepStatus(b.beatId);
            return (
              <button key={b.beatId} onClick={() => setOpenBeat(openBeat === b.beatId ? null : b.beatId)}
                style={{ ...card, marginBottom: 0, padding: 8, cursor: "pointer", textAlign: "left",
                  outline: openBeat === b.beatId ? "2px solid #2f855a" : "none" }}>
                <div style={{ position: "relative", aspectRatio: "9/16", background: "#f3f4f6", borderRadius: 6, overflow: "hidden", marginBottom: 6 }}>
                  {/* The captioned card (frame + on-screen text) when the beat has one, else the raw frame. */}
                  {(b.cardUrl ?? b.imageUrl)
                    ? <img src={(b.cardUrl ?? b.imageUrl)!} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", ...muted }}>no image</div>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>Segment {(b.position ?? 0) + 1}</div>
                {b.cardUrl && b.imageUrl && (
                  <a href={b.imageUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                    style={{ fontSize: 11, color: "#2f855a" }}>raw frame</a>
                )}
                {ss && <div style={{ fontSize: 11, color: stepStatusColor(ss === "in progress" ? "running" : ss) }}>{ss}</div>}
              </button>
            );
          })}
        </div>
      )}

      {/* LEVEL 3 — segment detail, inline under the strip */}
      {openBeat && (
        <SegmentDetailPanel
          arcId={arcId} seq={seq} beatId={openBeat} cmsId={cmsId}
          runId={video.runId} steps={steps} onChanged={() => { loadBoard(); loadSteps(); }}
        />
      )}
    </div>
  );
}

function ApproveButton({ arcId, seq, onDone }: { arcId: string; seq: number; onDone: () => void }) {
  const { track } = useProgress();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <button style={btnPrimary} disabled={busy} onClick={async () => {
        setBusy(true); setNote(null);
        try { await track("Approving", growthReviewApi.approve(arcId, seq), { detail: `video ${seq + 1} — the next stage starts`, done: () => "approved — it continues in the engine" }); onDone(); }
        catch (e) { setNote(String((e as Error).message ?? e)); }
        finally { setBusy(false); }
      }}>{busy ? "Approving…" : "Approve → advance"}</button>
      {note && <span style={{ fontSize: 12, color: "#b91c1c" }}>{note}</span>}
    </span>
  );
}

// CANCEL the video's live run. Confirm-then-act (like RevertRow): it ends the run for good — a new
// produce starts a fresh one. Ingest refuses a run that already finished (the note shows the 409).
function CancelRunButton({ arcId, seq, onDone }: { arcId: string; seq: number; onDone: () => void }) {
  const { track } = useProgress();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  if (!confirming)
    return <button style={btn} onClick={() => { setConfirming(true); setNote(null); }}>Cancel run</button>;
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: 12 }}>End this run?</span>
      <button style={btn} disabled={busy} onClick={async () => {
        setBusy(true); setNote(null);
        try { await track("Cancelling the run", growthReviewApi.cancel(arcId, seq), { detail: `video ${seq + 1}` }); setConfirming(false); onDone(); }
        catch (e) { setNote(String((e as Error).message ?? e)); }
        finally { setBusy(false); }
      }}>{busy ? "Cancelling…" : "Yes, cancel"}</button>
      <button style={btn} disabled={busy} onClick={() => setConfirming(false)}>Keep</button>
      {note && <span style={{ fontSize: 12, color: "#b91c1c" }}>{note}</span>}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL 3 — SEGMENT DETAIL: every render input; editable axes wired, frame/motion deferred.
// ═══════════════════════════════════════════════════════════════════════════
function SegmentDetailPanel({ arcId, seq, beatId, cmsId, runId, steps, onChanged }: {
  arcId: string; seq: number; beatId: string; cmsId: string | null;
  runId: string | null; steps: RunStep[] | null; onChanged: () => void;
}) {
  const { track } = useProgress();
  const [detail, setDetail] = useState<SegmentDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!cmsId) return;
    setErr(null);
    growthReviewApi.segment(arcId, seq, beatId, cmsId)
      .then(setDetail).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [arcId, seq, beatId, cmsId]);
  useEffect(load, [load]);

  const inputs = (detail?.inputs ?? {}) as Record<string, unknown>;
  const beatSteps = (steps ?? []).filter((s) => s.beatId === beatId);

  return (
    <div style={{ ...card, background: "#fafafa" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontWeight: 700 }}>Segment detail · {beatId}</div>
        <button style={btn} onClick={load}>Reload</button>
      </div>

      {!cmsId && <div style={muted}>Segment inputs load once the run's document is known (open a produced video).</div>}
      {err && <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {cmsId && !detail && !err && <div style={muted}>Loading inputs…</div>}

      {detail && (
        <div style={{ display: "grid", gap: 10 }}>
          {/* READ — script */}
          <InputRow label="Script"><ReadValue value={pick(inputs, ["spokenText", "scriptAst"])} /></InputRow>

          {/* EDITABLE — on-screen text */}
          <EditableTextRow
            label="On-screen text"
            current={asText(pick(inputs, ["onScreenText"]))}
            onSave={(text) => track("Saving the on-screen text", growthReviewApi.editText(arcId, seq, beatId, text), { detail: beatId }).then(() => { load(); onChanged(); })}
          />

          {/* EDITABLE (deferred redirect) — frame & motion shown as first-class rows.
              The edit affordance is present but disabled: true redirect (editing the direction) needs
              the pantry station (re-keying). Built here so lighting it up later is additive. */}
          <DeferredEditRow label="Frame (shot / angle / subject)" value={pick(inputs, ["frameAst"])} />
          <DeferredEditRow label="Motion (camera / subject motion)" value={pick(inputs, ["motionAst"])} />

          {/* READ — voice, cast, duration, transitions */}
          <InputRow label="Voice"><ReadValue value={pick(inputs, ["audioIntent", "audioRefUrl"])} /></InputRow>
          <InputRow label="Cast"><ReadValue value={pick(inputs, ["castBinding", "castRef"])} /></InputRow>
          <InputRow label="Duration"><ReadValue value={pick(inputs, ["durationIntent", "durationSec"])} /></InputRow>
          <InputRow label="Transitions"><ReadValue value={pick(inputs, ["transitionIn", "transitionOut"])} /></InputRow>

          {/* ARTEFACT — the rendered image/clip for this segment */}
          <InputRow label="Artefact"><ArtefactView production={detail.production} /></InputRow>

          {/* REGENERATE (re-roll) — re-run this segment's image with the same inputs, new seed */}
          <RegenerateRow arcId={arcId} seq={seq} beatId={beatId}
            // prefill the subject editor with what the frame depicts now (frameAst.slots.subject.text)
            subjectText={asText(((pick(inputs, ["frameAst"]) as { slots?: { subject?: { text?: unknown } } } | undefined)
              ?.slots?.subject?.text) ?? "") || null}
            onDone={() => { load(); onChanged(); }} />

          {/* REVERT (post → pre) — send a FINISHED segment back to production: reset its steps + re-stitch,
              keeping the other segments. Only offered when this segment is currently post (all steps done)
              and the video has a run. After it fires, the roll-up shows post_pre (videoState computes it). */}
          {runId && beatSteps.length > 0 && beatSteps.every((s) => s.status === "succeeded" || s.status === "skipped" || s.status === "done") && (
            <RevertRow runId={runId} beatId={beatId} onDone={() => { load(); onChanged(); }} />
          )}

          {/* STEPS — per-segment engine status + the existing interventions (retry/skip/reseed) */}
          {beatSteps.length > 0 && (
            <div style={{ borderTop: "1px solid #eee", paddingTop: 8 }}>
              <div style={{ ...muted, marginBottom: 4 }}>Steps</div>
              {beatSteps.map((s) => (
                <SegmentStepRow key={s.rowId} step={s} runId={runId} cmsId={cmsId} onActed={onChanged} />
              ))}
            </div>
          )}

          {/* CAST EDIT (edit-continue) — swap who this segment shows, then re-flow */}
          {runId && <CastEditToggle runId={runId} onDone={onChanged} />}
        </div>
      )}
    </div>
  );
}

// ── small presentational helpers ──────────────────────────────────────────────
function InputRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, alignItems: "start" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}
function ReadValue({ value }: { value: unknown }) {
  if (value == null || (typeof value === "object" && Object.keys(value as object).length === 0))
    return <span style={muted}>—</span>;
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 0);
  return <div style={{ fontSize: 12, fontFamily: "ui-monospace, monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#fff", border: "1px solid #eee", borderRadius: 6, padding: 6 }}>{text}</div>;
}
function pick(obj: Record<string, unknown>, keys: string[]): unknown {
  const out: Record<string, unknown> = {};
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) out[k] = obj[k];
  const ks = Object.keys(out);
  return ks.length === 0 ? null : ks.length === 1 ? out[ks[0]] : out;
}
function asText(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && "text" in (v as object)) return String((v as { text?: unknown }).text ?? "");
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

function EditableTextRow({ label, current, onSave }: { label: string; current: string; onSave: (t: string) => Promise<unknown> }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(current);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => { setVal(current); }, [current]);
  return (
    <InputRow label={label}>
      {!editing ? (
        <div style={{ display: "flex", gap: 8, alignItems: "start" }}>
          <div style={{ flex: 1 }}><ReadValue value={current || null} /></div>
          <button style={btn} onClick={() => setEditing(true)}>Edit</button>
        </div>
      ) : (
        <div>
          <textarea style={{ ...input, minHeight: 60 }} value={val} onChange={(e) => setVal(e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
            <button style={btnPrimary} disabled={busy} onClick={async () => {
              setBusy(true); setNote(null);
              try { await onSave(val); setEditing(false); }
              catch (e) { setNote(String((e as Error).message ?? e)); }
              finally { setBusy(false); }
            }}>{busy ? "Saving…" : "Save"}</button>
            <button style={btn} onClick={() => { setVal(current); setEditing(false); }}>Cancel</button>
            {note && <span style={{ fontSize: 12, color: "#b91c1c" }}>{note}</span>}
          </div>
        </div>
      )}
    </InputRow>
  );
}

// Frame/motion: shown, with an edit affordance that's present but deferred (redirect needs the station).
function DeferredEditRow({ label, value }: { label: string; value: unknown }) {
  return (
    <InputRow label={label}>
      <div style={{ display: "flex", gap: 8, alignItems: "start" }}>
        <div style={{ flex: 1 }}><ReadValue value={value} /></div>
        <button style={{ ...btn, cursor: "not-allowed", opacity: 0.55 }} disabled
          title="Redirecting the visual direction is coming — it needs the formula (pantry) station to re-key the segment. For now, re-roll regenerates with the same direction.">
          Redirect (soon)
        </button>
      </div>
    </InputRow>
  );
}

function ArtefactView({ production }: { production: Record<string, unknown> | null }) {
  if (!production) return <span style={muted}>no artefact yet</span>;
  const clip = (production.clip as { url?: string } | undefined)?.url;
  const image = (production.image as { url?: string } | undefined)?.url
    ?? (production.card as { url?: string } | undefined)?.url;
  if (clip) return <video src={clip} controls style={{ maxWidth: 220, borderRadius: 6 }} />;
  if (image) return <img src={image} alt="" style={{ maxWidth: 160, borderRadius: 6 }} />;
  return <span style={muted}>no artefact yet</span>;
}

function RegenerateRow({ arcId, seq, beatId, subjectText, onDone }: { arcId: string; seq: number; beatId: string; subjectText?: string | null; onDone: () => void }) {
  const { track } = useProgress();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [editing, setEditing] = useState(false);
  const runRegen = async (subject?: string) => {
    setBusy(true); setNote(null); setOk(false); setEditing(false);
    try {
      const r = await track("Regenerating the frame", growthReviewApi.regenerate(arcId, seq, beatId, subject),
        { steps: ["Send the request", "The engine re-makes the frame (usually 20–60 s)", "Done"], detail: subject ? `with the new subject: ${subject}` : beatId });
      setNote(`${subject ? "Regenerating with edited subject" : "Re-rolling"} — ${r.regenerationsUsed}/${r.regenerationsMax} used`); setOk(true);
      onDone();
    } catch (e) { setNote(String((e as Error).message ?? e)); setOk(false); }
    finally { setBusy(false); }
  };
  return (
    <InputRow label="Regenerate">
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button style={btn} disabled={busy} onClick={() => runRegen()}>{busy ? "Working…" : "Re-roll this segment"}</button>
        <button style={btn} disabled={busy} onClick={() => setEditing(true)}>Edit subject & regenerate</button>
        <span style={muted}>re-roll = same direction; edit = change what the frame depicts</span>
        {note && <span style={{ fontSize: 12, color: ok ? "#2f855a" : "#b91c1c" }}>{note}</span>}
      </div>
      {editing && (
        <PromptEditModal
          title={`Edit subject — segment ${beatId}`}
          initial={subjectText ?? ""}
          onCancel={() => setEditing(false)}
          onSubmit={(v) => runRegen(v)}
        />
      )}
    </InputRow>
  );
}

// REVERT a finished segment back to production. Calls the existing per-beat re-render (opsApi.reRenderBeat)
// — which resets this beat's steps + the whole-video render/stitch and revives the run, keeping siblings.
// Confirm-then-act: it discards this segment's finished render to redo it, so a stray click shouldn't fire.
function RevertRow({ runId, beatId, onDone }: { runId: string; beatId: string; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  return (
    <InputRow label="Send back">
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {!confirming ? (
          <>
            <button style={btn} onClick={() => setConfirming(true)}>Send this segment back to production</button>
            <span style={muted}>reworks just this segment, keeps the rest</span>
          </>
        ) : (
          <>
            <span style={{ fontSize: 13 }}>Discard this segment's render and rework it?</span>
            <button style={{ ...btn, borderColor: "#b45309", color: "#b45309" }} disabled={busy} onClick={async () => {
              setBusy(true); setNote(null);
              try { await opsApi.reRenderBeat(runId, beatId); onDone(); }
              catch (e) { setNote(String((e as Error).message ?? e)); }
              finally { setBusy(false); setConfirming(false); }
            }}>{busy ? "Sending back…" : "Yes, send back"}</button>
            <button style={btn} disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </>
        )}
        {note && <span style={{ fontSize: 12, color: "#b91c1c" }}>{note}</span>}
      </div>
    </InputRow>
  );
}

function SegmentStepRow({ step: s, cmsId, onActed }: {
  step: RunStep; runId: string | null; cmsId: string | null; onActed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); onActed(); } catch (e) { setErr(String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  };
  const actionable = s.status === "failed" || s.status === "awaiting_gate";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 13 }}>
      <div style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{s.stepId}</span>
        <span style={{ color: stepStatusColor(s.status), marginLeft: 8 }}>{s.status}</span>
        {s.attemptNumber > 1 && <span style={{ color: "#9ca3af", marginLeft: 6 }}>· attempt {s.attemptNumber}</span>}
        {s.failureCategory && <span style={{ color: "#b91c1c", marginLeft: 6 }}>· {s.failureCategory}</span>}
        {err && <span style={{ color: "#b91c1c", marginLeft: 6 }}>· {err}</span>}
      </div>
      {actionable && (
        <div style={{ display: "flex", gap: 6 }}>
          <button style={{ ...btn, padding: "3px 8px", fontSize: 12 }} disabled={busy}
            onClick={() => act(() => opsApi.retryStep(s.rowId))}>retry</button>
          <button style={{ ...btn, padding: "3px 8px", fontSize: 12 }} disabled={busy}
            onClick={() => act(() => opsApi.skipStep(s.rowId))}>skip</button>
          {IDENTITY_STEP_IDS.has(s.stepId) && s.beatId && cmsId && (
            <button style={{ ...btn, padding: "3px 8px", fontSize: 12, borderColor: "#b45309", color: "#b45309" }} disabled={busy}
              title="Discard this identity's redress and create a fresh one from the same look"
              onClick={() => act(() => opsApi.reseedStep(s.rowId, cmsId, s.beatId!))}>reseed</button>
          )}
        </div>
      )}
    </div>
  );
}

function CastEditToggle({ runId, onDone }: { runId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: "1px solid #eee", paddingTop: 8 }}>
      <button style={btn} onClick={() => setOpen((o) => !o)}>{open ? "Close cast edit" : "Change who this segment shows →"}</button>
      {open && <EditCastPanel runId={runId} onDone={() => { setOpen(false); onDone(); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ARC-LEVEL score panel (unchanged — growth_arc_score has no per-video row, so it sits at arc level).
// ═══════════════════════════════════════════════════════════════════════════
function ArcScorePanel({ arcId }: { arcId: string }) {
  const { track } = useProgress();
  const [score, setScore] = useState<ArcScore | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const [readErr, setReadErr] = useState<string | null>(null);
  // (tf3-31, A17) only a 404 is "not measured yet"; any other failure is shown
  const load = useCallback(() => {
    setReadErr(null);
    growthApi.getArcScore(arcId).then(setScore).catch((e) => setReadErr(String((e as Error)?.message ?? e)));   // catch-reason: the error is shown in the panel
  }, [arcId]);
  useEffect(load, [load]);

  const measure = async () => {
    setBusy(true); setNote(null);
    try { const r = await track("Measuring the arc", growthApi.measureArc(arcId), { detail: "reading each post's metrics" }); setScore(r.score); setReadErr(null); }
    catch (e) { setNote(String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, background: "#f9fafb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Arc performance</div>
          {readErr ? (
            <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>The arc's score couldn't be read: {readErr}</div>
          ) : score === undefined ? (
            <div style={{ ...muted, marginTop: 4 }}>Loading…</div>
          ) : score === null ? (
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>Not measured yet — measurable once the arc's videos have been posted.</div>
          ) : (
            <div style={{ ...muted, marginTop: 4 }}>
              Score <b style={{ color: "#111827" }}>{score.score ?? "—"}</b>
              {score.followersGained != null && <> · {score.followersGained} followers gained</>}
              {score.windowFrom && <> · window {new Date(score.windowFrom).toLocaleDateString()}{score.windowTo ? `–${new Date(score.windowTo).toLocaleDateString()}` : ""}</>}
            </div>
          )}
          {note && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>{note}</div>}
        </div>
        <button style={btn} disabled={busy} onClick={measure}>{busy ? "Measuring…" : score ? "Re-measure" : "Measure now"}</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EDIT-CAST PANEL (edit-then-continue) — preserved from the prior page, relocated into segment detail.
// Named facet ("who this segment shows"), not step ids. Rejects the open gate → patches castRefs →
// cascades from casting. IDs (cmsId, gateRowId, segmentId) auto-derived; character pick is a roster.
// ═══════════════════════════════════════════════════════════════════════════
function EditCastPanel({ runId, onDone }: { runId: string; onDone: () => void }) {
  const [cmsId, setCmsId] = useState<string | null>(null);
  const [gateRowId, setGateRowId] = useState<string | undefined>(undefined);
  const [segments, setSegments] = useState<Array<{ id: string; orderIndex: number; castRefs: string[] | null }> | null>(null);
  const [segmentId, setSegmentId] = useState<string>("");
  const [roster, setRoster] = useState<CharacterListItem[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    opsApi.runSteps(runId)
      .then((r) => {
        setCmsId(r.run?.cmsId ?? null);
        const gate = r.steps.find((s) => s.status === "awaiting_gate");
        setGateRowId(gate?.rowId);
        if (!r.run?.cmsId) setLoadErr("This run has no CMS document yet — nothing to edit until it reaches casting.");
      })
      .catch((e) => setLoadErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setLoadErr)
    adminApi.browseCharacters({ limit: 200 }).then((r) => setRoster(r.characters)).catch(() => {});
  }, [runId]);

  useEffect(() => {
    if (!cmsId) return;
    opsApi.cmsSegments(cmsId)
      .then((r) => {
        setSegments(r.segments);
        if (r.segments.length) { setSegmentId(r.segments[0].id); setPicked(r.segments[0].castRefs ?? []); }
      })
      .catch((e) => setLoadErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setLoadErr)
  }, [cmsId]);

  const onPickSegment = (id: string) => {
    setSegmentId(id);
    setPicked(segments?.find((s) => s.id === id)?.castRefs ?? []);
  };
  const toggleChar = (stockId: string) =>
    setPicked((p) => p.includes(stockId) ? p.filter((x) => x !== stockId) : [...p, stockId]);

  const apply = async () => {
    if (!cmsId || !segmentId) return;
    setBusy(true); setNote(null);
    try {
      const r = await opsApi.editContinue(runId, {
        gateRowId, facet: "segment.castRefs", cmsId, segmentId,
        value: picked.length ? picked : null, reviewerId: "reviewer",
      });
      if (r.error) setNote(`Failed at ${r.step}: ${r.error}`);
      else { setNote(`Applied — cascaded from ${r.cascadedFrom}. The run is re-flowing.`); setTimeout(onDone, 1200); }
    } catch (e) { setNote("Failed: " + String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 12, padding: 12, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Change who a segment shows</div>
      <p style={{ ...muted, marginBottom: 10 }}>Rejects the open review gate, sets the segment's cast, and re-renders only what depends on it.</p>

      {loadErr && <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 8 }}>{loadErr}</div>}
      {!loadErr && !cmsId && <div style={muted}>Loading…</div>}

      {cmsId && (
        <>
          <label style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            Which part of the video
            {segments === null ? <span style={{ color: "#9ca3af" }}> — loading segments…</span>
              : segments.length === 0 ? <span style={{ color: "#9ca3af" }}> — this video has no segments to cast</span>
              : (
                <select style={{ ...input, marginTop: 4 }} value={segmentId} onChange={(e) => onPickSegment(e.target.value)}>
                  {segments.map((s) => (
                    <option key={s.id} value={s.id}>Segment {s.orderIndex + 1}{s.castRefs?.length ? ` · ${s.castRefs.length} cast` : ""}</option>
                  ))}
                </select>
              )}
          </label>

          {segments && segments.length > 0 && (
            <>
              <div style={{ ...muted, marginBottom: 6 }}>Who's in it (click to toggle)</div>
              {roster === null ? <div style={{ fontSize: 12, color: "#9ca3af" }}>Loading characters…</div> : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 160, overflowY: "auto", marginBottom: 10 }}>
                  {roster.map((c) => {
                    const on = picked.includes(c.stockId);
                    return (
                      <button key={c.stockId} onClick={() => toggleChar(c.stockId)}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 999, cursor: "pointer",
                          border: `1px solid ${on ? "#2f855a" : "#d1d5db"}`, background: on ? "#ecfdf5" : "#fff", fontSize: 12 }}>
                        {c.previewUrl
                          ? <img src={c.previewUrl} alt="" width={20} height={20} style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover" }} />
                          : <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#eee", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{(c.displayName || "?").charAt(0)}</span>}
                        {c.displayName || "Unnamed"}{on ? " ✓" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button style={btnPrimary} disabled={busy || !segmentId} onClick={apply}>
                  {busy ? "Applying…" : `Apply & re-flow${picked.length ? ` (${picked.length})` : " — clear cast"}`}
                </button>
                {note && <span style={{ fontSize: 12, color: note.startsWith("Failed") ? "#b91c1c" : "#2f855a" }}>{note}</span>}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

